import "components/pages/game/GameBoardTile.css";

import { Box, Tooltip } from "@mui/material";
import { TileType } from "models/TileType";
import React from "react";
import { STORAGE_BASE_URL } from "utils/constants";
import { playerColorToBackgroundHex } from "utils/styling_helpers";

export interface Props {
  tile: TileType;
  onClick: () => void; // Prop for handling tile clicks
  style: React.CSSProperties;
}

const standardTiles = new Set([
  "STANDARD_CITY",
  "STANDARD_OCEAN",
  "SOLAR_OCEAN",
  "STANDARD_GREENERY",
]);

const landClaim = "LAND_CLAIM";
const capital = "CAPITAL";

export const Component: React.FC<Props> = ({ tile, onClick, style }) => {
  const bonuses: string[] = [];
  Object.entries(tile.resources).forEach(([resource, count]) => {
    for (let i = 0; i < (count || 0); i++) {
      bonuses.push(resource);
    }
  });
  Object.entries(tile.production).forEach(([resource, count]) => {
    for (let i = 0; i < (count || 0); i++) {
      bonuses.push(`${resource}_production`);
    }
  });
  if (tile.cards && tile.cards > 0) {
    bonuses.push(...Array(tile.cards).fill("card"));
  }

  const tileTooltip =
    tile.type === "NONE"
      ? ""
      : `Tile: ${tile.plain_name}` +
        (tile.type === "OCEAN" ? "" : `\n\nOwner: ${tile.owner?.name ?? "Neutral"}`);

  const hasOwner = (tile: TileType) => {
    return (
      (tile.owner && tile.name !== landClaim && tile.owner.name) || standardTiles.has(tile.name)
    );
  };

  const isOcean = (tile: TileType) => {
    return tile.isOceanLocation || tile.isOcean;
  };

  const isSpecial = (tile: TileType) => {
    return !standardTiles.has(tile.name) && tile.name !== landClaim;
  };

  const isCity = (tile: TileType) => {
    return tile.isCity && tile.name !== capital;
  };

  const isCapital = (tile: TileType) => {
    return tile.name === capital;
  };

  const isGreenery = (tile: TileType) => {
    return tile.isGreenery;
  };

  const isEmpty = (tile: TileType) => {
    return tile.isEmpty;
  };

  return (
    <Tooltip title={tileTooltip} placement="top">
      <Box
        className="hexagon"
        onClick={onClick}
        sx={{
          ...style,
        }}
      >
        <Box
          className={`hexinner ${isOcean(tile) ? "ocean" : ""} ${
            hasOwner(tile) ? "has-owner" : "no-owner"
          } ${isSpecial(tile) ? "special" : ""} ${isCity(tile) ? "city" : ""} ${
            isCapital(tile) ? "capital" : ""
          } ${isGreenery(tile) ? "greenery" : ""}`}
        >
          <Box className="hexcontent">{tile.displayName}</Box>
          <Box className="placement_bonuses">
            {bonuses.map((bonus, index) => (
              <Box key={index} className={`bonus${index}`}>
                <img src={`${STORAGE_BASE_URL}/icons/${bonus}_sm.jpg`} width="14px" alt={bonus} />
              </Box>
            ))}
            {tile.hasPlacementDecision && tile.placementDecisionName === "SOUTH_POLE" && (
              <Box className="south-pole">
                <img
                  src={`${STORAGE_BASE_URL}/icons/money_sm.jpg`}
                  width="20px"
                  alt="money-icon"
                  className="money-icon"
                />
                <img
                  src={`${STORAGE_BASE_URL}/icons/ocean.jpg`}
                  width="20px"
                  alt="ocean-icon"
                  className="ocean-icon"
                />
                <Box className="minus-money">-6</Box>
              </Box>
            )}
            {tile.hasPlacementDecision && tile.placementDecisionName === "WILDCARD_RESOURCE" && (
              <Box className={`wildcard-resource`}>
                <img
                  src={`${STORAGE_BASE_URL}/icons/wildcard_resource_sm.jpg`}
                  width="14px"
                  alt={"wildcard-resource-icon"}
                />
              </Box>
            )}
          </Box>
          {!isEmpty(tile) && tile.name !== landClaim && (
            <Box className="image-icon">
              <img src={tile.imageUrl} width="20px" alt={tile.name} />
            </Box>
          )}
          {tile.owner && tile.owner.color && (
            <Box
              sx={{
                height: "20px",
                width: "20px",
                backgroundColor: playerColorToBackgroundHex(tile.owner.color),
              }}
            />
          )}
        </Box>
      </Box>
    </Tooltip>
  );
};

Component.displayName = "GameBoardTile";
